import {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type FC,
} from 'react';
import {
  Button,
  Flex,
  Loading,
  ProgressBar,
  Select,
  Text,
  ButtonGroup,
} from '@/_components';
import { getSentQuestionnairesUsageDataForTaxYear } from '@/_api/admin-questionnaires';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import styles from './LowQuestionnaireUsagePercentage.module.css';
import { ALL_ENTITY_TYPE_ITEMS } from '@/_constants/entities';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const LowQuestionnaireUsagePercentage: FC = () => {
  const { preferences, updateFirmPreference } = useContext(FirmSettingsContext);
  const { setSending } = useContext(QuestionnaireContext);
  const { permissions, token } = useContext(UserContext);

  const [selectedEntityType, setSelectedEntityType] = useState<string>('All');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [
    activeEntityWithQuestionnaireCount,
    setActiveEntityWithQuestionnaireCount,
  ] = useState<number>(0);
  const [
    filteredEntityIdsWithoutQuestionnaire,
    setFilteredEntityIdsWithoutQuestionnaire,
  ] = useState<number[]>([]);
  const [activeEntityCount, setActiveEntityCount] = useState<number>(0);

  const showQuestionnaireUsageAlert =
    preferences?.show_questionnaire_usage_alert;

  const fetchSentQuestionnairesCount = useCallback(async () => {
    try {
      setIsLoading(true);

      const {
        companies_count,
        questionnaires_count,
        entities_by_id_without_questionnaire,
      }: any = await getSentQuestionnairesUsageDataForTaxYear({
        entityType: selectedEntityType,
        taxYear: 2024,
        token,
      });

      setActiveEntityCount(companies_count);
      setActiveEntityWithQuestionnaireCount(questionnaires_count);
      setFilteredEntityIdsWithoutQuestionnaire(
        entities_by_id_without_questionnaire,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEntityType, token]);

  useEffect(() => {
    if (showQuestionnaireUsageAlert) {
      fetchSentQuestionnairesCount();
    }
  }, [fetchSentQuestionnairesCount, showQuestionnaireUsageAlert]);

  const percentageSent = useMemo(() => {
    const percentage = activeEntityCount
      ? (activeEntityWithQuestionnaireCount / activeEntityCount) * 100
      : 0;
    return percentage;
  }, [activeEntityCount, activeEntityWithQuestionnaireCount]);

  const handleSelectEntityType = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedEntityType(event.target.value);
  };

  const handleSelectWithoutQuestionnaire = () => {
    setSending(true, filteredEntityIdsWithoutQuestionnaire);
  };

  const hasSendQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.add'],
    permissions,
  );

  return (
    <>
      {showQuestionnaireUsageAlert ? (
        <Flex
          className={styles.lowQuestionnaireUsagePercentage}
          gap={4}
          isYCentered={true}
          isColumn={true}
          isInline={true}
        >
          {isLoading ? (
            <Loading size={16} />
          ) : (
            <>
              <Flex gap={6} isYCentered={true} isFullWidth={true}>
                <Select
                  label="Entity Type"
                  isLabelHidden={true}
                  selectedValue={selectedEntityType}
                  onChange={handleSelectEntityType}
                  items={ALL_ENTITY_TYPE_ITEMS}
                />
                <Text type="small" lineClamp={2}>
                  {`You sent questionnaires to ${activeEntityWithQuestionnaireCount} out of ${activeEntityCount} active entities (${percentageSent.toFixed(0)}%) for the 2024 tax year. Please reach out if you need assistance.`}
                </Text>
                <ButtonGroup isSameLine={true}>
                  <Button
                    value="Send"
                    intent="primary"
                    size="small"
                    isDisabled={!hasSendQuestionnairePermission}
                    onClick={handleSelectWithoutQuestionnaire}
                    tooltip={`Select ${selectedEntityType} without a 2024 questionnaire`}
                    withTooltip={true}
                  />
                  <Button
                    value="Hide"
                    intent="ghost"
                    size="small"
                    onClick={() =>
                      updateFirmPreference(
                        'show_questionnaire_usage_alert',
                        false,
                      )
                    }
                  />
                </ButtonGroup>
              </Flex>
              <Flex gap={6} isYCentered={true} isFullWidth={true}>
                <ProgressBar
                  value={percentageSent}
                  max={100}
                  intent={
                    percentageSent >= 75
                      ? 'success'
                      : percentageSent <= 25
                        ? 'danger'
                        : 'warning'
                  }
                />
              </Flex>
            </>
          )}
        </Flex>
      ) : (
        <ButtonGroup>
          <Button
            value="Show 2024 Questionnaire Usage"
            intent="secondary"
            size="small"
            onClick={() =>
              updateFirmPreference('show_questionnaire_usage_alert', true)
            }
          />
        </ButtonGroup>
      )}
    </>
  );
};
